"use client"

import type React from "react"
import { useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, Alert } from "reactstrap"
import { VehicleVisitService } from "../../../Service/TicketingService"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
}

const VehicleEntryModal: React.FC<Props> = ({ isOpen, toggle, onSuccess }) => {
  const [plate, setPlate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!plate.trim()) {
      setError("Please enter a plate number")
      return
    }

    try {
      setLoading(true)
      setError("")

      const response = await VehicleVisitService.recordEntry(plate.trim().toUpperCase())

      if (response.success) {
        await Swal.fire({
          title: "Entry Recorded!",
          text: response.message,
          icon: "success",
          html: `
            <div class="text-start">
              <p><strong>Plate:</strong> ${response.plate}</p>
              <p><strong>Gate:</strong> ${response.gate}</p>
              <p><strong>Entry Time:</strong> ${new Date(response.entered_at).toLocaleString()}</p>
              <p><strong>Visit ID:</strong> ${response.visit_id}</p>
            </div>
          `,
        })
        setPlate("")
        onSuccess()
      }
    } catch (error: any) {
      console.error("Failed to record entry:", error)
      const errorMessage = error.response?.data?.message || "Failed to record entry"
      setError(errorMessage)

      if (error.response?.data?.existing_visit) {
        await Swal.fire({
          title: "Vehicle Already Inside",
          text: `This vehicle entered on ${new Date(error.response.data.existing_visit.entered_at).toLocaleString()}`,
          icon: "warning",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setPlate("")
    setError("")
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose}>Record Vehicle Entry</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="plate">Plate Number *</Label>
            <Input
              name="plate"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="Enter vehicle plate number"
              autoFocus
            />
          </FormGroup>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <div className="text-end">
            <Button color="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Record Entry"}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default VehicleEntryModal
